/**
 * Content library. Pictures and sounds from the repo assets folder,
 * grouped the way the library panel is laid out: stickers, backgrounds, images, audio.
 */

import { useMemo, useState, type DragEvent } from 'react';
import { LIBRARY_ASSETS } from '@/lib/video/libraryAssets';
import { libraryGroupLabel, type LibraryAsset } from '@/lib/video/libraryCatalog';
import { STOCK_ITEMS } from '@/lib/video/stockLibrary';

type LibraryFilter = 'all' | 'audio' | 'visuals';

const VISUAL_ORDER = ['video', 'sticker', 'background', 'image'] as const;

function drag(event: DragEvent, payload: Record<string, string>): void {
  const body = JSON.stringify(payload);
  event.dataTransfer.setData('application/x-craftara-clip', body);
  event.dataTransfer.setData('text/plain', body);
  event.dataTransfer.effectAllowed = 'copy';
}

function matches(item: LibraryAsset, query: string): boolean {
  if (!query) return true;
  return `${item.title} ${libraryGroupLabel(item.group)}`.toLowerCase().includes(query);
}

export default function LibraryBrowser({
  onPlace,
  onGradient,
  onTone,
}: {
  onPlace: (id: string) => void;
  onGradient: (id: string) => void;
  onTone: () => void;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<LibraryFilter>('all');
  const [audioGroup, setAudioGroup] = useState('all');
  const [visualGroup, setVisualGroup] = useState('all');
  const [menu, setMenu] = useState<'audio' | 'visuals' | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({ background: true, video: true });
  const needle = query.trim().toLowerCase();

  const audioGroups = useMemo(() => {
    const names = new Set(LIBRARY_ASSETS.filter((item) => item.section === 'audio').map((item) => item.group));
    return ['all', ...Array.from(names).sort()];
  }, []);

  const visualSections = VISUAL_ORDER.filter((section) => visualGroup === 'all' || visualGroup === section);
  const showVisuals = filter !== 'audio';
  const showAudio = filter === 'audio';
  const soundGroups = audioGroup === 'all' ? audioGroups.filter((group) => group !== 'all') : [audioGroup];

  const pictures = (section: (typeof VISUAL_ORDER)[number]): LibraryAsset[] =>
    LIBRARY_ASSETS.filter((item) => item.section === section && matches(item, needle));

  const sounds = (group: string): LibraryAsset[] =>
    LIBRARY_ASSETS.filter((item) => item.section === 'audio' && item.group === group && matches(item, needle));

  const toggle = (id: string): void => setOpen((current) => ({ ...current, [id]: !current[id] }));
  const colorItems = STOCK_ITEMS.filter((item) => item.kind === 'gradient' && (!needle || item.title.toLowerCase().includes(needle)));
  const showColors = showVisuals && (visualGroup === 'all' || visualGroup === 'background') && colorItems.length > 0;

  return (
    <div className="vt-library">
      <h2>Content library</h2>
      <input
        className="vt-browser-search"
        placeholder="Search"
        aria-label="Search the content library"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      <div className="vt-library-filters">
        <button
          type="button"
          className={filter === 'all' ? 'active' : ''}
          onClick={() => {
            setFilter('all');
            setMenu(null);
          }}
        >
          All
        </button>
        <div className="vt-library-menu">
          <button
            type="button"
            className={filter === 'audio' ? 'active' : ''}
            aria-expanded={menu === 'audio'}
            onClick={() => {
              setFilter('audio');
              setMenu(menu === 'audio' ? null : 'audio');
            }}
          >
            Audio ▾
          </button>
          {menu === 'audio' ? (
            <div className="vt-library-pop" role="menu">
              {audioGroups.map((group) => (
                <button
                  key={group}
                  type="button"
                  onClick={() => {
                    setAudioGroup(group);
                    setFilter('audio');
                    setMenu(null);
                    setOpen((current) => ({ ...current, [group]: true }));
                  }}
                >
                  {group === 'all' ? 'All audio' : libraryGroupLabel(group)}
                </button>
              ))}
            </div>
          ) : null}
        </div>
        <div className="vt-library-menu">
          <button
            type="button"
            className={filter === 'visuals' ? 'active' : ''}
            aria-expanded={menu === 'visuals'}
            onClick={() => {
              setFilter('visuals');
              setMenu(menu === 'visuals' ? null : 'visuals');
            }}
          >
            Visuals ▾
          </button>
          {menu === 'visuals' ? (
            <div className="vt-library-pop" role="menu">
              {(['all', ...VISUAL_ORDER] as const).map((group) => (
                <button
                  key={group}
                  type="button"
                  onClick={() => {
                    setVisualGroup(group);
                    setFilter('visuals');
                    setMenu(null);
                    if (group !== 'all') setOpen((current) => ({ ...current, [group]: true }));
                  }}
                >
                  {group === 'all' ? 'All visuals' : libraryGroupLabel(group)}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      {showVisuals
        ? visualSections.map((section) => {
            const items = pictures(section);
            const expanded = Boolean(open[section]) || Boolean(needle);
            if (!items.length && section === 'image' && !needle) {
              return (
                <section key={section} className="vt-library-block">
                  <button type="button" className="vt-library-section" onClick={() => toggle(section)}>
                    <span>Images</span>
                    <span aria-hidden="true">{expanded ? '⌄' : '›'}</span>
                  </button>
                  {expanded ? <p className="vt-browser-empty">No images in the library yet.</p> : null}
                </section>
              );
            }
            if (!items.length && !(section === 'background' && showColors)) return null;
            return (
              <section key={section} className="vt-library-block">
                <button type="button" className="vt-library-section" onClick={() => toggle(section)}>
                  <span>{libraryGroupLabel(section)}</span>
                  <span aria-hidden="true">{expanded ? '⌄' : '›'}</span>
                </button>
                {expanded ? (
                  <div className="vt-library-strip">
                    {section === 'background' && showColors
                      ? colorItems.map((item) => (
                          <button
                            key={item.id}
                            type="button"
                            className="vt-library-thumb"
                            title={item.title}
                            draggable
                            onDragStart={(event) => drag(event, { action: 'gradient', id: item.id })}
                            onClick={() => onGradient(item.id)}
                          >
                            <span style={{ background: item.swatch }} />
                          </button>
                        ))
                      : null}
                    {items.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className="vt-library-thumb"
                        title={item.title}
                        draggable
                        onDragStart={(event) => drag(event, { action: 'library', id: item.id })}
                        onClick={() => onPlace(item.id)}
                      >
                        {item.section === 'video' ? (
                          <video src={`${item.url}#t=0.1`} muted playsInline preload="metadata" />
                        ) : (
                          <img src={item.url} alt="" />
                        )}
                      </button>
                    ))}
                  </div>
                ) : null}
              </section>
            );
          })
        : null}

      {showAudio ? (
        <>
          {!needle ? (
            <section className="vt-library-block">
              <button type="button" className="vt-library-section" onClick={() => toggle('tones')}>
                <span>Tones</span>
                <span aria-hidden="true">{open.tones ? '⌄' : '›'}</span>
              </button>
              {open.tones ? (
                <div className="vt-library-audio">
                  <button type="button" draggable onDragStart={(event) => drag(event, { action: 'tone' })} onClick={onTone}>
                    Soft bed
                  </button>
                </div>
              ) : null}
            </section>
          ) : null}
          {soundGroups.map((group) => {
            const items = sounds(group);
            if (!items.length) return null;
            const expanded = Boolean(open[group]) || Boolean(needle);
            return (
              <section key={group} className="vt-library-block">
                <button type="button" className="vt-library-section" onClick={() => toggle(group)}>
                  <span>{libraryGroupLabel(group)}</span>
                  <span aria-hidden="true">{expanded ? '⌄' : '›'}</span>
                </button>
                {expanded ? (
                  <div className="vt-library-audio">
                    {items.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        draggable
                        onDragStart={(event) => drag(event, { action: 'library', id: item.id })}
                        onClick={() => onPlace(item.id)}
                      >
                        {item.title}
                      </button>
                    ))}
                  </div>
                ) : null}
              </section>
            );
          })}
        </>
      ) : null}
    </div>
  );
}
